import React from 'react'

import { combineClasses } from '~/util'
import { Title, SubTitle } from '../titles'

import LogoSVG from '~/assets/logomark-is-sad.svg?react'

import './NotFound.scss'

export interface NotFoundProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * A custom title to display. Generally, you should not pass this so the
   * default error messaging is shown unless you need something more
   * descriptive.
   */
  title?: string
  /**
   * Customize the subtitle. Generally you don't need to pass this unless you
   * need to provide a more detailed message.
   */
  subtitle?: string
  /**
   * The name of the thing that was not found. It will be used in the display
   * message so we maintain consistent error messaging.
   *
   * Won't be used if you pass a custom title.
   */
  lostThing?: string
  /**
   * The community contact number for communities that have one.
   */
  phoneNumber?: string
  /**
   * Additional CSS class to add to the subtitle element.
   */
  classNameSubTitle?: string
}

/**
 * Renders a consistent "Not Found" message.
 */
export function NotFound({
  title = "Yikes...We're Lost!",
  subtitle,
  lostThing,
  phoneNumber,
  className,
  classNameSubTitle = '',
  ...rest
}: NotFoundProps) {
  if (!subtitle && (lostThing || phoneNumber)) {
    subtitle = `I can't find that${lostThing ? ' ' + lostThing : ''}. `
    subtitle += `Please check the link you followed is correct. `
    // TODO Allow other contact methods
    if (phoneNumber) {
      subtitle += `Or give us a call at ${phoneNumber}.`
    }
  }

  return (
    <div
      data-testid="notFound"
      className={combineClasses('not-found', className)}
      {...rest}
    >
      <Title className="title-large" data-testid="title">
        {title}
      </Title>
      {subtitle && (
        <SubTitle
          className={combineClasses('title-small', classNameSubTitle)}
          data-testid="subtitle"
        >
          {subtitle}
        </SubTitle>
      )}
      <LogoSVG className="logo" />
    </div>
  )
}
